import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Loader2, AlertCircle, Crown } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // 'checking', 'success', 'error', 'processing'
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { refreshUser } = useAuth();

  const sessionId = searchParams.get('session_id');
  const maxAttempts = 5;
  const pollInterval = 2000; // 2 seconds

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('error');
      toast({
        title: "Payment Status Check Timeout",
        description: "Please check your email for confirmation or contact support.",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await apiClient.get(`/subscription/status/${sessionId}`);
      const data = response.data;

      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        
        // Refresh user data to get updated subscription status
        await refreshUser();
        
        toast({
          title: "Payment Successful! 🎉",
          description: "Welcome to ItsMyVibe Pro! You now have unlimited access.",
          variant: "default"
        });
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        toast({
          title: "Payment Session Expired",
          description: "Please try upgrading again.",
          variant: "destructive"
        });
        return;
      }

      // If payment is still processing, continue polling
      setStatus('processing');
      setAttempts(prev => prev + 1);
      
      setTimeout(() => {
        pollPaymentStatus();
      }, pollInterval);
      
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
      
      toast({
        title: "Error Checking Payment Status",
        description: "Please contact support if you completed the payment.",
        variant: "destructive"
      });
    }
  };

  const handleContinue = () => {
    navigate('/dashboard');
  };

  const handleRetry = () => {
    navigate('/dashboard');
  };

  if (status === 'checking' || status === 'processing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mb-4">
              <Loader2 className="w-8 h-8 text-violet-600 animate-spin" />
            </div>
            <CardTitle className="text-xl">
              {status === 'checking' ? 'Checking Payment Status...' : 'Payment Processing...'}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-slate-600">
              {status === 'checking' 
                ? 'Please wait while we verify your payment...'
                : 'Your payment is being processed. This may take a few moments...'
              }
            </p>
            {paymentData && (
              <div className="bg-slate-50 p-3 rounded-lg text-sm">
                <div className="font-medium">Payment Details:</div>
                <div>Amount: ${paymentData.amount}</div>
                <div>Status: {paymentData.payment_status}</div>
                <div>Attempt: {attempts}/{maxAttempts}</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-xl text-green-700">Payment Successful!</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            <div className="space-y-2">
              <div className="flex items-center justify-center space-x-2">
                <Crown className="w-5 h-5 text-violet-600" />
                <span className="font-semibold text-violet-600">Welcome to ItsMyVibe Pro!</span>
              </div>
              <p className="text-slate-600">
                You now have unlimited access to projects and AI improvements.
              </p>
            </div>

            {paymentData && (
              <div className="bg-violet-50 p-4 rounded-lg text-sm">
                <div className="font-medium text-violet-700 mb-2">Subscription Details:</div>
                <div className="space-y-1 text-violet-600">
                  <div>Plan: Pro Monthly</div>
                  <div>Amount: ${paymentData.amount}/month</div>
                  <div>Status: Active</div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <div className="text-sm text-slate-600">
                <div className="font-medium mb-1">What's included:</div>
                <ul className="text-left space-y-1">
                  <li>✅ Unlimited projects</li>
                  <li>✅ Unlimited AI improvements</li>
                  <li>✅ Priority support</li>
                  <li>✅ Early access to new features</li>
                </ul>
              </div>
              
              <Button 
                className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                onClick={handleContinue}
              >
                Continue to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <CardTitle className="text-xl text-red-700">Payment Issue</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-slate-600">
            We encountered an issue processing your payment. If you completed the payment, 
            please check your email for confirmation or contact our support team.
          </p>
          
          {paymentData && (
            <div className="bg-red-50 p-3 rounded-lg text-sm">
              <div className="font-medium text-red-700">Payment Status:</div>
              <div>Session: {sessionId?.substring(0, 20)}...</div>
              <div>Status: {paymentData.payment_status || 'Unknown'}</div>
            </div>
          )}

          <div className="space-y-2">
            <Button 
              variant="outline" 
              className="w-full"
              onClick={handleRetry}
            >
              Back to Dashboard
            </Button>
            <div className="text-xs text-slate-500">
              Need help? Contact support with session ID: {sessionId?.substring(0, 20)}...
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;